/* ─── Display components: Text, Image, Icon, Divider, Markdown, Code, TextConsole ─── */

import { useCallback, useEffect, useLayoutEffect, useRef, type CSSProperties } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import remarkGfm from 'remark-gfm';
import hljs from 'highlight.js';
import { formatTextual } from '../textual';
import { useArtifactSendEvent } from '../store';
import { COLOR_MAP } from '../colors';

// ─── Text ───────────────────────────────────────────────────────────────

const VARIANT_MAP: Record<string, keyof JSX.IntrinsicElements> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  body: 'p',
  caption: 'small',
  overline: 'span',
  mono: 'span',
};

const HEADLINE_VARIANTS = new Set(['h1', 'h2', 'h3']);
const MONO_VARIANTS = new Set(['h4', 'h5', 'overline', 'mono']);

export function ArtifactText({
  content,
  variant = 'body',
  color,
  bold = false,
  italic = false,
}: {
  content?: unknown;
  variant?: string;
  color?: string | null;
  bold?: boolean;
  italic?: boolean;
}) {
  const Tag = VARIANT_MAP[variant] ?? 'p';
  const isHeadline = HEADLINE_VARIANTS.has(variant);
  const isMono = MONO_VARIANTS.has(variant);

  return (
    <Tag
      className={`a-text a-text-${variant}`}
      style={{
        fontFamily: isHeadline
          ? 'var(--artifact-font-headline)'
          : isMono
            ? 'var(--artifact-font-mono)'
            : 'var(--artifact-font)',
        fontWeight: bold ? 700 : isHeadline ? 400 : undefined,
        fontStyle: italic ? 'italic' : undefined,
        color: color ? (COLOR_MAP[color] ?? color) : 'var(--artifact-foreground)',
        margin: 0,
      }}
    >
      {formatTextual(content)}
    </Tag>
  );
}

// ─── Image ──────────────────────────────────────────────────────────────

const IMAGE_SIZE_WIDTHS: Record<string, number> = {
  thumbnail: 70,
  small: 227,
  medium: 340,
  large: 680,
};

export function ArtifactImage({
  url = '',
  caption,
  background,
  size = 'full',
  pixelated = false,
}: {
  url?: string;
  caption?: string;
  background?: string | null;
  size?: string;
  pixelated?: boolean;
}) {
  const presetWidth = IMAGE_SIZE_WIDTHS[size];
  const figureStyle: CSSProperties = { margin: 0 };
  if (presetWidth !== undefined) figureStyle.width = presetWidth;
  return (
    <figure className="a-image" style={figureStyle}>
      <img
        src={url}
        alt={caption ?? ''}
        draggable={false}
        style={{
          width: '100%',
          maxWidth: '100%',
          borderRadius: 'var(--artifact-border-radius)',
          display: 'block',
          background: background ?? undefined,
          imageRendering: pixelated ? 'pixelated' : undefined,
        }}
      />
      {caption && (
        <figcaption
          style={{
            fontSize: 12,
            color: 'var(--artifact-foreground)',
            opacity: 0.6,
            marginTop: 6,
            fontFamily: 'var(--artifact-font)',
          }}
        >
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

// ─── Icon (Material Icons) ──────────────────────────────────────────────

export function ArtifactIcon({ name = '' }: { name?: string }) {
  return (
    <span
      className="material-icons a-icon"
      style={{
        fontSize: 24,
        color: 'var(--artifact-foreground)',
        verticalAlign: 'middle',
      }}
    >
      {name}
    </span>
  );
}

// ─── Divider ────────────────────────────────────────────────────────────

export function ArtifactDivider({
  axis = 'horizontal',
}: {
  axis?: string;
}) {
  if (axis === 'vertical') {
    return (
      <div
        className="a-divider a-divider-vertical"
        style={{
          width: 1,
          alignSelf: 'stretch',
          background: 'var(--artifact-muted)',
          minHeight: 20,
        }}
      />
    );
  }
  return (
    <hr
      className="a-divider a-divider-horizontal"
      style={{
        border: 'none',
        borderTop: '1px solid var(--artifact-muted)',
        width: '100%',
        margin: '4px 0',
      }}
    />
  );
}

// ─── Markdown ───────────────────────────────────────────────────────────

export function ArtifactMarkdown({ text = '' }: { text?: string }) {
  return (
    <div
      className="a-markdown"
      style={{
        fontFamily: 'var(--artifact-font)',
        color: 'var(--artifact-foreground)',
        lineHeight: 1.6,
        fontSize: 14,
      }}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
        {text}
      </ReactMarkdown>
    </div>
  );
}

// ─── Code ───────────────────────────────────────────────────────────────

export function ArtifactCode({
  source = '',
  language = 'python',
  executable,
  __node_id,
}: {
  source?: string;
  language?: string;
  executable?: boolean;
  __node_id?: string;
}) {
  const codeRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (codeRef.current && source) {
      codeRef.current.removeAttribute('data-highlighted');
      try {
        hljs.highlightElement(codeRef.current);
      } catch {
        // language not registered — render plain text
      }
    }
  }, [source, language]);

  return (
    <div className="a-code">
      <div className="a-code-header">
        <span className="a-code-lang">{language}</span>
        {executable && __node_id && (
          <button type="button" className="a-code-run">
            ▶ Run
          </button>
        )}
      </div>
      <pre className="a-code-pre">
        <code ref={codeRef} className={`language-${language}`}>
          {source}
        </code>
      </pre>
    </div>
  );
}

// ─── TextConsole ────────────────────────────────────────────────────────

export function ArtifactTextConsole({
  lines,
  linenos = false,
  __node_id,
}: {
  lines?: string[];
  linenos?: boolean;
  __node_id?: string;
}) {
  const sendEvent = useArtifactSendEvent();
  const items = Array.isArray(lines) ? lines : [];
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll only if already near the bottom
  const wasAtBottomRef = useRef(true);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      wasAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    };
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el && wasAtBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [items.length]);

  const gutterWidth = linenos ? `${Math.max(3, String(items.length).length + 1)}ch` : undefined;

  const handleMouseUp = useCallback(() => {
    if (!__node_id) return;
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !sel.rangeCount) return;
    const text = sel.toString();
    if (!text) return;
    const container = scrollRef.current;
    if (!container) return;
    const lineEls = container.querySelectorAll('[data-line-index]');
    let first = items.length;
    let last = 0;
    for (const el of lineEls) {
      if (sel.containsNode(el, true)) {
        const idx = Number((el as HTMLElement).dataset.lineIndex);
        if (idx < first) first = idx;
        if (idx > last) last = idx;
      }
    }
    sendEvent(__node_id, 'text_selected', {
      kind: 'ConsoleTextSelected', text, start_line: first, end_line: last,
    });
  }, [__node_id, sendEvent, items.length]);

  return (
    <div
      ref={scrollRef}
      className={`a-text-console ${linenos ? 'a-text-console-linenos' : ''}`}
      onMouseUp={handleMouseUp}
    >
      {items.map((line, i) => (
        <div
          key={i}
          className="a-text-console-line"
          data-line-index={i}
          onClick={() => {
            if (__node_id) sendEvent(__node_id, 'line_clicked', {
              kind: 'ConsoleLineClicked', line, index: i,
            });
          }}
        >
          {linenos && (
            <span className="a-text-console-gutter" style={{ minWidth: gutterWidth }}>
              {i + 1}
            </span>
          )}
          <span className="a-text-console-content">{line}</span>
        </div>
      ))}
    </div>
  );
}
